import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
    Button,
    Typography,
    TextField,
    DialogActions,
    Box,
    Grid
} from '@mui/material';
import axios from 'axios';

const cardStyle = {
    style: {
        base: {
            fontSize: '16px',
            color: '#fff',
            '::placeholder': {
                color: 'rgba(255, 255, 255, 0.6)',
            },
        },
        invalid: {
            color: '#fa755a',
        },
    },
};

const CardUpdateForm = ({ user, onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    // Initialize name from user prop
    const [name, setName] = useState(`${user.firstName} ${user.lastName}`);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        if (!stripe || !elements) {
            setLoading(false);
            return;
        }

        const cardElement = elements.getElement(CardElement);

        try {
            // ⭐ STEP 1: Safely GET or CREATE the Stripe Customer ID
            // This robust call handles users who are missing the Stripe ID by creating it
            // and saving it to MongoDB, then returning the ID.
            const customerResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/stripe/get-or-create-customer`, {
                firebaseUid: user.firebaseUid, // Required for server lookup
                email: user.email,
                name: name, // Use the name from the input field
            });

            const stripeCustomerId = customerResponse.data.id;

            // 2. Create the new Payment Method ID (pm_...)
            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name,
                    email: user.email,
                },
            });

            if (stripeError) {
                setError(stripeError.message);
                setLoading(false);
                return;
            }

            // 3. Attach payment method and set as default
            const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/stripe/attach-payment-method`, {
                customerId: stripeCustomerId, // Use the ID guaranteed by Step 1
                paymentMethodId: paymentMethod.id,
            });

            if (res.data.success) {
                // Success: Notify parent component to close the dialog
                if (onSuccess) onSuccess();
            } else {
                setError('Failed to update payment method.');
            }
        } catch (err) {
            console.error(err);
            // Use the error message from the server response if available
            const errorMessage = err.response?.data?.error || 'Something went wrong while processing payment.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label="Name on Card"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        margin="normal"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: 'rgba(255, 255, 255, 0.6)' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box
                        sx={{
                            padding: '16px',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '6px',
                            backgroundColor: '#2c2c2c',
                            marginTop: '0.5rem',
                            minHeight: '64px',
                        }}
                    >
                        <CardElement options={cardStyle} />
                    </Box>
                </Grid>

                {error && (
                    <Grid item xs={12}>
                        <Typography color="error" sx={{ mt: 2 }}>
                            {error}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <DialogActions sx={{ mt: 3, p: 0 }}>
                        <Button onClick={onCancel} style={{ color: '#12c099' }}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!stripe || loading}
                            style={{ color: '#12c099' }}
                        >
                            {loading ? 'Updating...' : 'Update'}
                        </Button>
                    </DialogActions>
                </Grid>
            </Grid>
        </form>
    );
};

export default CardUpdateForm;